import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { Notice } from "./Notice";

/**
 * The three states every Machine tab shares, so thirteen tabs behave the same
 * way when they are loading, when they fail, and when they hold nothing.
 *
 * They are here rather than in each tab because thirteen hand-written empty
 * states drift, and a reader who sees a different empty state on each tab
 * cannot tell "nothing to do" from "something is broken".
 */
export function TabSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-4" aria-label="Loading">
      <Skeleton className="h-10 w-full" />
      {Array.from({ length: rows }, (_, row) => (
        <Skeleton key={row} className="h-36 w-full" />
      ))}
    </div>
  );
}

/**
 * A failed read, with the retry beside it.
 *
 * The retry matters more here than on a read-only dashboard: these tabs are a
 * work queue, and a reader who cannot retry has to reload the whole portal and
 * lose their place in it.
 */
export function QueryErrorState({
  title,
  message = "The request failed, so nothing is shown.",
  onRetry,
}: {
  title: string;
  message?: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-col items-start gap-3">
      <Notice title={title}>{message}</Notice>
      <Button type="button" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

/**
 * A tab's own heading.
 *
 * This is NOT the shell's chrome: the shell draws the module name, the tab bar
 * and the freshness line, and this sits inside the viewport under all of it.
 * It exists because "Review queue" alone does not say what the reader is
 * looking at or what they are meant to do with it, and thirteen tabs of
 * unexplained tables is the failure this surface already had once.
 */
export function TabHeading({
  title,
  description,
  aside,
}: {
  title: string;
  description: string;
  aside?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h2 className="text-lg font-semibold text-ks-ink">{title}</h2>
        <p className="mt-1 max-w-prose text-sm text-muted-foreground">{description}</p>
      </div>
      {aside && <div className="text-[13px] font-medium tabular-nums text-muted-foreground">{aside}</div>}
    </div>
  );
}

/**
 * Nothing here — said in the caller's own words.
 *
 * `label` is required rather than defaulted: "No rows" is true of an empty
 * queue and of a broken filter alike, and only the caller knows which question
 * was asked.
 */
export function EmptyCard({ label }: { label: string }) {
  return (
    <Card>
      <CardContent className="py-8 text-center text-sm text-muted-foreground">{label}</CardContent>
    </Card>
  );
}
